import { z } from 'zod';
import { conversationGroupMessageScheme, conversationMessageScheme } from '@/models/conversations/conversation';
import { avatarUrlSchema, nameSchema } from '@/utils/schemas';

export enum ReceiveMessageCommand {
  HeartBeatResponse = 'HeartBeatRes',
  LoginResponse = 'LoginRes',
  LogoutResponse = 'LogoutRes',
  KickOut = 'LetLogout',
  ChatMessage = 'ChatMsg',
  NewChatSession = 'NewChatSession',
  ReadChat = 'ReadChat',
  ReadGroupChat = 'ReadGroupChat',
  ChatResponse = 'ChatRes',
  GroupChatResponse = 'GroupChatRes',
  GroupChatMessage = 'GroupChatMsg',
  GroupNewApplication = 'GroupApplyJoin',
  DeleteChatMessage = 'DeletChatMsg',
  DeleteGroupChatMessage = 'DeleteGroupChatMsg',
  DeleteUserMessage = 'DeleteUserAllMsg',
  Connected = 'Connected',
  Group = 'Group',
  UpdateUserToken = 'UpdateWebUserToken',
  FriendApplication = 'FriendApplication',
  Online = 'OnLine',
  Offline = 'OffLine',
  ReadChatSelf = 'ReadChatSelf',
  BanGroupUser = 'BanGroupUser',
}

export enum ReceiveGroupMessageCommand {
  Join = 'Join',
  Leave = 'Leave',
  Remove = 'Remove',
  Purge = 'DeleteUserAllChat',
}

export const receiveMessageScheme = z.discriminatedUnion('Cmd', [
  z.object({
    Cmd: z.literal(ReceiveMessageCommand.ChatMessage),
    chat: conversationMessageScheme,
  }),
  z.object({
    Cmd: z.literal(ReceiveMessageCommand.GroupChatMessage),
    GroupID: z.number(),
    GroupImg: avatarUrlSchema,
    GroupInfo: z.string().nullish(),
    GroupName: nameSchema,
    groupchat: conversationGroupMessageScheme,
  }),
  z.object({
    Cmd: z.literal(ReceiveMessageCommand.ChatResponse),
    Code: z.number(),
    ChatID: z.number(),
    ChatSessionID: z.number(),
    ClientMsgId: z.string().optional(),
    IsDuplicated: z.boolean().default(false),
    Msg: z.string(),
    TimeStamp: z.number(),
  }),
  z.object({
    Cmd: z.literal(ReceiveMessageCommand.GroupChatResponse),
    Code: z.number(),
    GroupID: z.number(),
    GroupChatID: z.number(),
    ClientMsgId: z.string().optional(),
    IsDuplicated: z.boolean().default(false),
    Msg: z.string(),
    TimeStamp: z.number(),
  }),
  z.object({
    Cmd: z.literal(ReceiveMessageCommand.LoginResponse),
    Code: z.number(),
    UID: z.string(),
  }), // LoginResponse
  z.object({
    Cmd: z.literal(ReceiveMessageCommand.LogoutResponse),
    Code: z.number(),
    UID: z.string(),
  }), // LoginResponse
  z.object({
    Cmd: z.literal(ReceiveMessageCommand.ReadChat),
    Code: z.number(),
    ChatSessionID: z.number(),
    ChatID: z.string().or(z.number()).pipe(z.coerce.number()),
  }),
  z.object({
    Cmd: z.literal(ReceiveMessageCommand.ReadChatSelf),
    ChatSessionID: z.number(),
    ChatID: z.string().or(z.number()).pipe(z.coerce.number()),
    TimeStamp: z.number(),
  }),
  z.object({
    Cmd: z.literal(ReceiveMessageCommand.ReadGroupChat),
    GroupID: z.number(),
    MaxReadID: z.number(),
  }),
  z.object({
    Cmd: z.literal(ReceiveMessageCommand.HeartBeatResponse),
    Code: z.number(),
  }),
  z.object({
    Cmd: z.literal(ReceiveMessageCommand.NewChatSession),
    FriendID: z.number(),
    ChatSessionID: z.number(),
  }),
  z.object({
    Cmd: z.literal(ReceiveMessageCommand.Group),
    Type: z.nativeEnum(ReceiveGroupMessageCommand),
    UserID: z.number().optional(),
    GroupID: z.number(),
    GroupName: nameSchema.optional(),
    GroupImg: avatarUrlSchema,
  }),
  z.object({
    Cmd: z.literal(ReceiveMessageCommand.BanGroupUser),
    GroupID: z.number(),
    UserID: z.number(),
    Status: z.union([z.literal(0), z.literal(1)]),
  }),
  z.object({
    Cmd: z.literal(ReceiveMessageCommand.UpdateUserToken),
    Token: z.string(),
  }),
  z.object({
    Cmd: z.literal(ReceiveMessageCommand.Online),
    FriendID: z.number(),
  }),
  z.object({
    Cmd: z.literal(ReceiveMessageCommand.Offline),
    FriendID: z.number(),
  }),
  z.object({
    Cmd: z.literal(ReceiveMessageCommand.Connected),
  }),
  z.object({
    Cmd: z.literal(ReceiveMessageCommand.KickOut),
  }),
  z.object({
    Cmd: z.literal(ReceiveMessageCommand.FriendApplication),
    ApplicationID: z.number(),
    UserID: z.number(),
    NickName: nameSchema,
    HeadImg: avatarUrlSchema,
    Remark: z.string().nullish(),
  }),
  z.object({
    Cmd: z.literal(ReceiveMessageCommand.DeleteChatMessage),
    ChatSessionID: z.number(),
    ChatID: z.number(), // message id
  }),
  z.object({
    Cmd: z.literal(ReceiveMessageCommand.DeleteGroupChatMessage),
    GroupID: z.number(),
    GroupChatID: z.number(), // message id
  }),
  z.object({
    Cmd: z.literal(ReceiveMessageCommand.DeleteUserMessage),
    UserID: z.number(),
  }),
  z.object({
    Cmd: z.literal(ReceiveMessageCommand.GroupNewApplication),
    GroupID: z.number(),
    GroupImg: avatarUrlSchema,
    GroupJoinApplyCount: z.number(),
    GroupName: nameSchema,
    ApplyUserID: z.number(),
  }),
]);

export type ReceiveMessageType = z.infer<typeof receiveMessageScheme>;
